import { useMemo, useState, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { royalcaninDogProducts, eukanubaProducts, nexgardProducts, simparicaProducts, bravectoProducts, purinaProducts, bobmartinProducts, formatZAR } from '../Components/ProductRetrieval'
import { filterProducts, sortProducts } from '../Utils/Product_filter_util'
import { groupByFormula } from '../Utils/weightVariantsUtil'
import Pagination from '../Components/Pagination/Pagination'
import { useCart } from '../Components/Cart'
import { useFilterParams, FilterBar } from '../Components/FilterBar'
import { dogFilterCategories } from '../Components/FilterBar/dogFilterCategories'

const ITEMS_PER_PAGE = 20

const DogProducts = () => {
  const { searchParams, setParam, setCurrentPage, resetFilters } = useFilterParams()
  const [justAddedId, setJustAddedId] = useState(null)
  const { addToCart } = useCart()

  const nexgardDogProducts = useMemo(() => nexgardProducts.filter(p => (p.additionalInfo?.pet_type || 'Dog') === 'Dog'), [])
  const simparicaDogProducts = useMemo(() => simparicaProducts.filter(p => (p.additionalInfo?.pet_type || 'Dog') === 'Dog'), [])
  const bravectoDogProducts = useMemo(() => bravectoProducts.filter(p => (p.additionalInfo?.pet_type || 'Dog') === 'Dog'), [])
  const purinaDogProducts = useMemo(() => purinaProducts.filter(p => (p.additionalInfo?.pet_type || 'Dog') === 'Dog'), [])
  const bobmartinDogProducts = useMemo(() => bobmartinProducts.filter(p => (p.additionalInfo?.pet_type || 'Dog') === 'Dog'), [])
  const dog_stuffs = useMemo(() => [...royalcaninDogProducts, ...eukanubaProducts, ...nexgardDogProducts, ...simparicaDogProducts, ...bravectoDogProducts, ...purinaDogProducts, ...bobmartinDogProducts], [nexgardDogProducts, simparicaDogProducts, bravectoDogProducts, purinaDogProducts, bobmartinDogProducts]);

  const searchTerm = searchParams.get('q') || ''
  const brand = searchParams.get('brand') || 'All'
  const category = searchParams.get('category') || 'All'
  const age = searchParams.get('age') || 'All'
  const breed = searchParams.get('breed') || 'All'
  const needs = searchParams.get('needs') || 'All'
  const size = searchParams.get('size') || 'All'
  const sterilised = searchParams.get('sterilised') || 'All'
  const productSize = searchParams.get('productSize') || 'All'
  const sortBy = searchParams.get('sort') || 'name-asc'
  const currentPage = parseInt(searchParams.get('page') || '1')

  const filteredProducts = useMemo(() => {
    const filterParams = { brand, category, age, breed, needs, size, sterilised, productSize }
    return filterProducts(dog_stuffs, searchTerm, filterParams)
  }, [dog_stuffs, brand, category, age, breed, needs, size, sterilised, productSize, searchTerm])

  const sortedAndGroupedProducts = useMemo(() => {
    return groupByFormula(sortProducts(filteredProducts, sortBy))
  }, [filteredProducts, sortBy])

  const totalPages = Math.ceil(sortedAndGroupedProducts.length / ITEMS_PER_PAGE)
  const paginatedProducts = sortedAndGroupedProducts.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  )

  const handleAddToCart = useCallback((product) => {
    addToCart(product.id, product.name)
    setJustAddedId(product.id)
    setTimeout(() => setJustAddedId(null), 2000)
  }, [addToCart])

  const handleClearFilters = useCallback(() => {
    resetFilters(dogFilterCategories.map((c) => c.key))
  }, [resetFilters])

  const activeFilterCount = [
    brand, category, age, breed, needs, size, sterilised, productSize
  ].filter(v => v !== 'All').length

  return (
    <div className="min-h-screen bg-[#faf9fe] text-gray-900">
      <section className="border-b border-gray-200 bg-gradient-to-br from-violet-700 via-violet-600 to-purple-800 px-4 py-14 text-white sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <p className="text-sm font-semibold uppercase tracking-widest text-violet-200">
            ArrAndArr
          </p>
          <h1 className="mt-2 text-4xl font-bold tracking-tight sm:text-5xl">
            Dog Products
          </h1>
          <p className="mt-4 max-w-2xl text-lg text-violet-100">
            Explore our curated range of dog food, treats, and care products — all priced in
            South African Rand and shipped locally.
          </p>
          <p className="mt-3 text-sm text-violet-200">
            All prices include VAT. Prices subject to change.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <FilterBar
          categories={dogFilterCategories}
          products={dog_stuffs}
          searchParams={searchParams}
          setParam={setParam}
          sortBy={sortBy}
          onSortChange={(v) => setParam('sort', v)}
          searchTerm={searchTerm}
          onSearchChange={(v) => setParam('q', v)}
          searchPlaceholder="Search dog products..."
          onClear={handleClearFilters}
          activeFilterCount={activeFilterCount}
          resultCount={paginatedProducts.length}
          totalCount={filteredProducts.length}
          resultUnit="dog products"
          colorScheme="violet"
        />

        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          colorScheme="violet"
        /> <br/>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {paginatedProducts.map((group) => {
            const weightVariants = group.variants
            const defaultVariant = weightVariants[0]
            const hasOutOfStock = weightVariants.some(v => v.inStock === false || v.inStock === 0)
            const hasPreorder = weightVariants.some(v => v.isPreOrder === true || v.inStock === 2)

            return (
              <article
                key={defaultVariant.id}
                className="group flex flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:-translate-y-1 hover:shadow-md"
              >
                <Link to={`/Product/${defaultVariant.id}`} className="flex flex-1 flex-col">
                  <div className="relative aspect-square bg-gradient-to-br from-gray-50 to-gray-100">
                    {defaultVariant.image ? (
                      <img
                        src={defaultVariant.image}
                        alt={group.baseName}
                        className={`h-full w-full object-contain max-h-[280px] ${hasOutOfStock ? 'grayscale' : ''}`}
                      />
                    ) : (
                      <div className="flex h-full flex-col items-center justify-center px-4 text-center">
                        <span className="text-sm font-medium text-gray-500">
                          No image available
                        </span>
                      </div>
                    )}
                    {hasOutOfStock && (
                      <span className="absolute left-3 top-3 rounded-full bg-gray-600 px-2.5 py-1 text-xs font-semibold text-white">
                        Out of stock
                      </span>
                    )}
                    {hasPreorder && !hasOutOfStock && (
                      <span className="absolute left-3 top-3 rounded-full bg-amber-500 px-2.5 py-1 text-xs font-semibold text-white">
                        Pre-order
                      </span>
                    )}
                  </div>

                  <div className="flex flex-1 flex-col p-4 text-left">
                    <h2 className={`line-clamp-2 text-base font-semibold ${
                      hasOutOfStock ? 'text-gray-500' : 'text-gray-900 group-hover:text-violet-600'
                    }`}>
                      {group.baseName}
                    </h2>
                    {defaultVariant.description && (
                      <p className="mt-1.5 line-clamp-2 text-xs text-gray-500 leading-relaxed">
                        {defaultVariant.description}
                      </p>
                    )}
                    {weightVariants.length > 1 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {weightVariants.map((v) => {
                          const label = v.name.match(/\(([^)]+)\)$/)?.[1] || `${v.weight}kg`
                          return (
                            <span
                              key={v.id}
                              className="rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-500"
                            >
                              {label}
                            </span>
                          )
                        })}
                      </div>
                    )}
                    <p className={`mt-auto pt-3 text-lg font-bold ${hasOutOfStock ? 'text-gray-400' : 'text-gray-900'}`}>
                      {formatZAR(defaultVariant.price)}
                    </p>
                  </div>
                </Link>

                <div className="px-4 pb-4">
                  <button
                    onClick={() => handleAddToCart(defaultVariant)}
                    disabled={hasOutOfStock && !hasPreorder}
                    className={`w-full rounded-lg px-4 py-2 text-sm font-semibold text-white transition ${
                      hasOutOfStock && !hasPreorder
                        ? 'bg-gray-400 cursor-not-allowed'
                        : justAddedId === defaultVariant.id
                        ? 'bg-violet-700'
                        : 'bg-violet-600 hover:bg-violet-700'
                    }`}
                  >
                    {justAddedId === defaultVariant.id
                      ? 'Added to cart'
                      : hasOutOfStock && !hasPreorder
                      ? 'Out of stock'
                      : 'Add to cart'}
                  </button>
                </div>
              </article>
            )
          })}
        </div>

        <Pagination
          totalPages={totalPages}
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          colorScheme="violet"
        />
      </section>
    </div>
  )
}

export default DogProducts
